import {
  Tag as BlueprintTag,
  TagProps as BlueprintTagProps,
} from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';

import {
  StaticAvatar,
  StaticAvatarProps,
} from 'bones/design-system/components/avatar/StaticAvatar';
import {
  InteractiveBadge,
  InteractiveBadgeProps,
} from 'bones/design-system/components/badge/InteractiveBadge';
import {
  ChipButton,
  ChipButtonProps,
} from 'bones/design-system/components/chip/ChipButton';
import { Body } from 'bones/design-system/components/typography/Body';

const AVATAR_SIZE_BY_CHIP_SIZE: Record<
  ChipProps['size'],
  StaticAvatarProps['size']
> = {
  xl: 'lg',
  lg: 'sm',
  md: 'xs',
  sm: 'xs',
};

const ICON_SIZE_BY_CHIP_SIZE: Record<
  ChipProps['size'],
  ChipButtonProps['iconSize']
> = {
  xl: 'sm',
  lg: 'sm',
  md: 'sm',
  sm: '2xs',
};

export type ChipProps = Omit<
  BlueprintTagProps,
  | 'children'
  | 'fill'
  | 'icon'
  | 'intent'
  | 'interactive'
  | 'large'
  | 'leftIcon'
  | 'minimal'
  | 'multiline'
  | 'onRemove'
  | 'rightIcon'
  | 'round'
> & {
  'data-testid'?: never;
  avatarProps?: Omit<StaticAvatarProps, 'active' | 'disabled' | 'size'>;
  badgeProps?: Omit<
    InteractiveBadgeProps,
    'active' | 'disabled' | 'selected' | 'size' | 'variant'
  >;

  disabled?: boolean;
  label?: string;
  leftButton?: Omit<ChipButtonProps, 'iconSize'>;
  rightButton?: Omit<ChipButtonProps, 'iconSize'>;
  selected?: boolean;
  size: 'xl' | 'lg' | 'md' | 'sm';
  testId?: string;
  value?: string;
};

export const Chip: React.VFC<ChipProps> = ({
  active,
  avatarProps,
  badgeProps,
  className,
  disabled,
  label,
  leftButton,
  rightButton,
  selected,
  size,
  testId,
  value,
  ...props
}) => (
  <BlueprintTag
    active={active}
    className={classNames(className, 'vb-chip', `vb-chip-size--${size}`, {
      'vb-chip-state--selected': selected,
      'vb-chip-state--disabled': disabled,
    })}
    data-testid={testId}
    icon={
      leftButton || avatarProps ? (
        <>
          {leftButton ? (
            <ChipButton
              iconSize={ICON_SIZE_BY_CHIP_SIZE[size]}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...leftButton}
            />
          ) : null}
          {avatarProps ? (
            <div className="vb-chip-avatar">
              <StaticAvatar
                disabled={disabled}
                size={AVATAR_SIZE_BY_CHIP_SIZE[size]}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...avatarProps}
              />
              {badgeProps ? (
                <InteractiveBadge
                  active={active}
                  disabled={disabled}
                  selected={selected}
                  size="xs"
                  variant="dark"
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...badgeProps}
                />
              ) : null}
            </div>
          ) : null}
        </>
      ) : undefined
    }
    rightIcon={
      rightButton ? (
        <ChipButton
          iconSize={ICON_SIZE_BY_CHIP_SIZE[size]}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...rightButton}
        />
      ) : null
    }
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  >
    {label ? (
      <Body className="vb-chip-label" size="sm">
        {label}
      </Body>
    ) : null}
    {value ? (
      <Body className="vb-chip-value" size="sm">
        {value}
      </Body>
    ) : null}
  </BlueprintTag>
);
